import { Injectable, computed, inject, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { finalize, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ActivityOverTime,
  DocumentMetric,
  LlmUsageMetric,
  LiveAssistantMetric,
  Metric,
  OrganisationActivityMinutes,
  OrgMetric,
  PracticeSessionMetric,
  SessionActivityOverTime,
  SubscriptionMetric,
  TopOrganisation,
  TopUser,
  UserMetric,
} from '../models/dashboard.types';

@Injectable({
  providedIn: 'root',
})
export class BackofficeDashboardService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  public errorMessage = signal<string | null>(null);
  private pendingRequests = signal(0);
  private inFlight = new Subscription();

  public connectionState = computed<'connecting' | 'connected' | 'error'>(() => {
    if (this.pendingRequests() > 0) return 'connecting';
    return this.errorMessage() ? 'error' : 'connected';
  });

  public systemOverview = signal<Metric[]>([]);
  public userMetrics = signal<UserMetric | null>(null);
  public organisationMetrics = signal<OrgMetric[]>([]);
  public practiceSessionMetrics = signal<PracticeSessionMetric[]>([]);
  public liveAssistantMetrics = signal<LiveAssistantMetric[]>([]);
  public documentMetrics = signal<DocumentMetric | null>(null);
  public subscriptionMetrics = signal<SubscriptionMetric | null>(null);
  public llmUsageMetrics = signal<LlmUsageMetric | null>(null);
  public topUsers = signal<{ topByPractice: TopUser[]; topByLive: TopUser[] } | null>(null);
  public topOrganisations = signal<TopOrganisation[]>([]);
  public organisationActivityMinutes = signal<OrganisationActivityMinutes[]>([]);
  public userActivityOverTime = signal<ActivityOverTime[]>([]);
  public sessionActivityOverTime = signal<SessionActivityOverTime[]>([]);

  loadData(startDate: Date, endDate: Date): void {
    // A new date range supersedes the previous batch. Cancelling it first stops its responses
    // from overwriting this range's data, and its finalize() from draining this batch's counter
    // — the counter is reset below, after those cancellations have already run.
    this.inFlight.unsubscribe();
    this.inFlight = new Subscription();
    this.errorMessage.set(null);
    const params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());

    const endpoints = [
      {
        url: 'system-overview',
        signal: this.systemOverview,
      },
      {
        url: 'user-metrics',
        signal: this.userMetrics,
      },
      {
        url: 'organisation-metrics',
        signal: this.organisationMetrics,
      },
      {
        url: 'practice-session-metrics',
        signal: this.practiceSessionMetrics,
      },
      {
        url: 'live-assistant-metrics',
        signal: this.liveAssistantMetrics,
      },
      {
        url: 'document-metrics',
        signal: this.documentMetrics,
      },
      {
        url: 'subscription-metrics',
        signal: this.subscriptionMetrics,
      },
      {
        url: 'llm-usage-metrics',
        signal: this.llmUsageMetrics,
      },
      {
        url: 'top-users',
        signal: this.topUsers,
      },
      {
        url: 'top-organisations',
        signal: this.topOrganisations,
      },
      {
        url: 'organisation-activity-minutes',
        signal: this.organisationActivityMinutes,
      },
      {
        url: 'user-activity-over-time',
        signal: this.userActivityOverTime,
      },
      {
        url: 'session-activity-over-time',
        signal: this.sessionActivityOverTime,
      },
    ];

    this.pendingRequests.set(endpoints.length);

    endpoints.forEach(({ url, signal }) => {
      this.inFlight.add(
        this.http
          .get<unknown>(`${this.apiUrl}/backoffice/dashboard/${url}`, { params })
          .pipe(finalize(() => this.pendingRequests.update((count) => count - 1)))
          .subscribe({
            next: (data) => (signal as WritableSignal<unknown>).set(data),
            error: (err) => {
              console.error(`Error loading ${url}:`, err);
              this.errorMessage.set(`Failed to load ${url}`);
            },
          }),
      );
    });
  }
}
